import Axios from 'axios';
import config from '../../config';
import { ISubscriptionsRequest } from '@/domain/ISubscriptions';
import store from '@/store';
import {
    IBirthdateRequest,
    IDeleteBonuscardRequest,
    IDeleteCommunicationDeviceRequest,
    ISaveBonuscardRequest,
    ISaveCommunicationDeviceRequest
} from '@/domain/ISaveInfo';
import { IAuthorizedPersonRequest } from '@/domain/ITravelManagerOrAuthorizedPerson';

export abstract class InfoApi {
    private static axios = Axios.create({
        baseURL: config.API_URL,
        headers: {
            common: {
                'Content-Type': 'application/json'
            }
        }
    });

    private static encodeQueryParam(value: string | number | null | undefined): string {
        return encodeURIComponent(String(value ?? ''));
    }

    static async subscribe(SubscriptionsRequest: ISubscriptionsRequest): Promise<any | null> {
        const url =
            '/list/subscribe?email_type=' +
            this.encodeQueryParam(SubscriptionsRequest.email_type) +
            '&email=' +
            this.encodeQueryParam(SubscriptionsRequest.email) +
            '&list=' +
            this.encodeQueryParam(SubscriptionsRequest.list);

        const encodedToken = store.state.token;
        try {
            const response = await this.axios.get<any>(url, { headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken } });
            return response;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async unsubscribe(SubscriptionsRequest: ISubscriptionsRequest): Promise<any | null> {
        const url =
            '/list/unsubscribe?email_type=' +
            this.encodeQueryParam(SubscriptionsRequest.email_type) +
            '&email=' +
            this.encodeQueryParam(SubscriptionsRequest.email) +
            '&list=' +
            this.encodeQueryParam(SubscriptionsRequest.list);

        const encodedToken = store.state.token;
        try {
            const response = await this.axios.get<any>(url, { headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken } });
            return response;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async saveBirthdate(BirthdateRequest: IBirthdateRequest): Promise<any | null> {
        const url =
            '/save_birthdate?customer_id=' +
            this.encodeQueryParam(BirthdateRequest.customerId) +
            '&birth_date=' +
            this.encodeQueryParam(BirthdateRequest.birthDate);
        const encodedToken = store.state.token;
        try {
            const response = await this.axios({
                method: 'put',
                url,
                headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken }
            });
            return response;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async saveCommDevice(SaveCommunicationDeviceRequest: ISaveCommunicationDeviceRequest): Promise<any | null> {
        let url =
            '/save_commdevice?customer_id=' +
            this.encodeQueryParam(SaveCommunicationDeviceRequest.customerId) +
            '&type=' +
            this.encodeQueryParam(SaveCommunicationDeviceRequest.type) +
            '&value=' +
            this.encodeQueryParam(SaveCommunicationDeviceRequest.value);
        const encodedToken = store.state.token;
        try {
            const isUpdate = SaveCommunicationDeviceRequest.communicationDeviceId !== '';
            url = `${url}${
                isUpdate ? '&communication_device_id=' + this.encodeQueryParam(SaveCommunicationDeviceRequest.communicationDeviceId) : ''
            }`;
            // const response = await this.axios.post<any>(url, { headers: { "Authentication-Estravel": 'Bearer ' + encodedToken } });
            const method = 'post';
            const response = await this.axios({
                method,
                url,
                headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken }
            });
            return response;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async deleteCommDevice(DeleteCommunicationDeviceRequest: IDeleteCommunicationDeviceRequest): Promise<any | null> {
        const url =
            '/delete_commdevice?customer_id=' +
            this.encodeQueryParam(DeleteCommunicationDeviceRequest.customerId) +
            '&communication_device_id=' +
            this.encodeQueryParam(DeleteCommunicationDeviceRequest.communicationDeviceId);
        const encodedToken = store.state.token;
        try {
            const response = await this.axios.delete<any>(url, { headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken } });
            return response;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async saveBonuscard(SaveBonuscardRequest: ISaveBonuscardRequest): Promise<any | null> {
        let url =
            '/save_bonuscard?customer_id=' +
            this.encodeQueryParam(SaveBonuscardRequest.customerId) +
            '&bonuscard_name=' +
            this.encodeQueryParam(SaveBonuscardRequest.bonuscardName) +
            '&bonuscard_number=' +
            this.encodeQueryParam(SaveBonuscardRequest.bonuscardNumber) +
            '&bonuscard_valid_until=' +
            this.encodeQueryParam(SaveBonuscardRequest.bonuscardValidUntil) +
            '&toplevel=' +
            this.encodeQueryParam(SaveBonuscardRequest.toplevel);
        const encodedToken = store.state.token;
        try {
            const isUpdate = !!SaveBonuscardRequest.bonuscardId;
            url = `${url}${isUpdate ? '&bonuscard_id=' + this.encodeQueryParam(SaveBonuscardRequest.bonuscardId) : ''}`;
            const method = 'post';
            const response = await this.axios({
                method,
                url,
                headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken }
            });
            return response;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async deleteBonuscard(DeleteBonuscardRequest: IDeleteBonuscardRequest): Promise<any | null> {
        const url =
            '/delete_bonuscard?customer_id=' +
            this.encodeQueryParam(DeleteBonuscardRequest.customerId) +
            '&bonuscard_id=' +
            this.encodeQueryParam(DeleteBonuscardRequest.bonuscardId);
        const encodedToken = store.state.token;
        try {
            const response = await this.axios.delete<any>(url, { headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken } });
            return response;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async addAuthorizedPerson(AuthorizedPersonRequest: IAuthorizedPersonRequest): Promise<any | null> {
        let url =
            '/add_authorized_person?customer_id=' +
            this.encodeQueryParam(AuthorizedPersonRequest.customer_id) +
            '&first_name=' +
            this.encodeQueryParam(AuthorizedPersonRequest.first_name) +
            '&last_name=' +
            this.encodeQueryParam(AuthorizedPersonRequest.last_name) +
            '&country_of_residence=' +
            this.encodeQueryParam(AuthorizedPersonRequest.country_of_residence) +
            '&language=' +
            this.encodeQueryParam(AuthorizedPersonRequest.language);
        if (AuthorizedPersonRequest.personal_code !== '') {
            url += '&personal_code=' + this.encodeQueryParam(AuthorizedPersonRequest.personal_code);
        }
        if (AuthorizedPersonRequest.birth_date !== '') {
            url += '&birth_date=' + this.encodeQueryParam(AuthorizedPersonRequest.birth_date);
        }
        if (AuthorizedPersonRequest.business_email !== '') {
            url += '&business_email=' + this.encodeQueryParam(AuthorizedPersonRequest.business_email);
        }
        if (AuthorizedPersonRequest.business_phone !== '') {
            url += '&business_phone=' + this.encodeQueryParam(AuthorizedPersonRequest.business_phone);
        }
        if (AuthorizedPersonRequest.business_mobile_phone !== '') {
            url += '&business_mobile_phone=' + this.encodeQueryParam(AuthorizedPersonRequest.business_mobile_phone);
        }
        if (AuthorizedPersonRequest.start_date !== '') {
            url += '&start_date=' + this.encodeQueryParam(AuthorizedPersonRequest.start_date);
        }
        if (AuthorizedPersonRequest.end_date !== '') {
            url += '&end_date=' + this.encodeQueryParam(AuthorizedPersonRequest.end_date);
        }
        const encodedToken = store.state.token;
        try {
            const method = 'post';
            return await this.axios({
                method,
                url,
                headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken }
            });
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return error.response;
        }
    }

    static async updateAuthorizedPerson(AuthorizedPersonRequest: IAuthorizedPersonRequest): Promise<any | null> {
        const url =
            '/update_authorized_person?customer_id=' +
            this.encodeQueryParam(AuthorizedPersonRequest.customer_id) +
            '&authperson_id=' +
            this.encodeQueryParam(AuthorizedPersonRequest.authperson_id) +
            '&country_of_residence=' +
            this.encodeQueryParam(AuthorizedPersonRequest.country_of_residence) +
            '&language=' +
            this.encodeQueryParam(AuthorizedPersonRequest.language) +
            '&business_email=' +
            this.encodeQueryParam(AuthorizedPersonRequest.business_email) +
            '&business_phone=' +
            this.encodeQueryParam(AuthorizedPersonRequest.business_phone) +
            '&business_mobile_phone=' +
            this.encodeQueryParam(AuthorizedPersonRequest.business_mobile_phone) +
            '&end_date=' +
            this.encodeQueryParam(AuthorizedPersonRequest.end_date);
        const encodedToken = store.state.token;
        try {
            const method = 'put';
            return await this.axios({
                method,
                url,
                headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken }
            });
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async addCardToWallet(request: { cardNumber: string; walletType: 'APPLE' | 'GOOGLE' }): Promise<any | null> {
        const endpoint = request.walletType === 'APPLE' ? 'applewallet' : 'googlewallet';
        const responseType = request.walletType === 'APPLE' ? 'arraybuffer' : undefined;
        const url = `/${endpoint}?lang=${this.encodeQueryParam('et')}&cardNumber=${this.encodeQueryParam(request.cardNumber)}`;
        const encodedToken = store.state.token;
        try {
            const method = 'get';
            return await this.axios({
                method,
                url,
                responseType,
                headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken }
            });
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async requestNewCard(): Promise<any | null> {
        const encodedToken = store.state.token;
        try {
            return await this.axios.post('/new_customercard', null, {
                headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken }
            });
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async addPersonalCode(request: { personalCode: string; customerId: string }): Promise<any | null> {
        const encodedToken = store.state.token;
        try {
            return await this.axios({
                method: 'post',
                url: 'add_personalcode',
                data: { personalCode: request.personalCode, customer_id: request.customerId },
                headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken }
            });
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async saveGender(request: { gender: 'F' | 'M'; customerId: string }): Promise<any | null> {
        const encodedToken = store.state.token;
        try {
            return await this.axios({
                method: 'post',
                url: 'save_sex',
                data: { sex: request.gender, customer_id: request.customerId },
                headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken }
            });
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async saveLanguage(request: { language: string; customerId: string }): Promise<any | null> {
        const encodedToken = store.state.token;
        try {
            return await this.axios({
                method: 'post',
                url: 'save_language',
                data: { language: request.language, customer_id: request.customerId },
                headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken }
            });
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }
}
